import React from "react";
import {graphql} from "react-apollo";
import {flowRight as compose} from "lodash";
import {withRouter} from "react-router";
import gql from "graphql-tag";
import {connect} from "react-redux";
import {fromJS, List, Map} from "immutable";
import Form from "./PurchaseOrderForm";
import {orderableProducts, filledLines} from "./LinesField";
import {cancelCreatePurchaseOrder, cancelEditPurchaseOrder, successEditPurchaseOrder} from "../../../actions/PurchaseOrderApiActions";
import {LIST_PARTNERS} from "../../../queries/partners";
import {LIST_PRODUCTS} from "../../../queries/inventory";
import {GET_PURCHASE_ORDER} from "../../../queries/purchaseOrders";
import {getAllWarehouses} from "../../../lib/Warehouses";
import {validatePresence} from "../../../lib/Validators";
import {createValidationErrors, createErrors} from "../../../lib/ErrorFormatter";
import Loading from "../../../components/Loading";
import GraphQLErrors from "../../../components/GraphQLErrors";

const HEADER = ["partnerId", "destinationWarehouseId", "poNumber", "supplierReference", "orderDate", "expectedDeliveryDate", "transportType", "carrierReference", "notes"];

// Creates a purchase order (header and lines), or edits the header of one
// when given `purchaseOrderId`.
class PurchaseOrderFormContainer extends React.Component {

  constructor(props) {
    super(props);
    this.state = { errors: [] }
  }

  isEdit() {
    return !!this.props.purchaseOrderId;
  }

  validate(values) {
    let errors = Map();
    errors = validatePresence(errors, values, "partnerId");
    errors = validatePresence(errors, values, "destinationWarehouseId");
    return errors.toJS();
  }

  handleSubmit(values) {
    const header = Map(HEADER.map((field) => [field, values.get(field) || ""]));
    const request = this.isEdit()
      ? this.props.updatePurchaseOrder(header.set("purchaseOrderId", this.props.purchaseOrderId).toJS())
      : this.props.createPurchaseOrder(header.set("lines", filledLines(values.get("lines", List()))).toJS());

    return request
      .then((response) => {
        const result = this.isEdit() ? response.data.updatePurchaseOrder : response.data.createPurchaseOrder;
        if (result.errors.length > 0) {
          this.setState({errors: createValidationErrors(result.errors, result.errors.map((error) => error.message).join("\n"))});
        } else if (this.isEdit()) {
          this.props.successEditPurchaseOrder();
        } else {
          this.props.cancelCreatePurchaseOrder();
          this.props.router.push(`/purchase-orders/${result.id}`);
        }
      })
      .catch((err) => this.setState({errors: createErrors(err)}));
  }

  handleCancel() {
    if (this.isEdit()) this.props.cancelEditPurchaseOrder();
    else this.props.cancelCreatePurchaseOrder();
  }

  initialValues() {
    const order = this.props.orderData && this.props.orderData.purchaseOrder;
    if (!order) return fromJS({partnerId: "", destinationWarehouseId: "", lines: [{}]});
    return fromJS(HEADER.reduce((acc, field) => ({...acc, [field]: order[field] || ""}), {}));
  }

  render() {
    const {suppliersData, productsData, orderData} = this.props;
    for (const data of [suppliersData, productsData, orderData]) {
      if (data && data.loading) return <Loading />
      if (data && data.error) return <GraphQLErrors error={data.error} />
    }

    return (
      <Form
        initialValues={this.initialValues()}
        warehouses={getAllWarehouses()}
        suppliers={fromJS(suppliersData.partners || [])}
        products={orderableProducts(fromJS(productsData.products || []))}
        showLines={!this.isEdit()}
        submitLabel={this.isEdit() ? "Save" : "Create"}
        errors={this.state.errors}
        validate={this.validate.bind(this)}
        onSubmit={this.handleSubmit.bind(this)}
        onCancel={this.handleCancel.bind(this)}
        />
    )
  }
}

const CREATE_PURCHASE_ORDER = gql`
  mutation createPurchaseOrder($data: CreatePurchaseOrderInput!) {
    createPurchaseOrder(input: $data) {
      id
      errors { key message }
    }
  }
`;

const UPDATE_PURCHASE_ORDER = gql`
  mutation updatePurchaseOrder($data: UpdatePurchaseOrderInput!) {
    updatePurchaseOrder(input: $data) {
      errors { key message }
    }
  }
`;

const withData = compose(
  graphql(LIST_PARTNERS, {
    name: "suppliersData",
    options: () => ({fetchPolicy: "network-only", variables: {role: "supplier", active: true}})
  }),
  graphql(LIST_PRODUCTS, {name: "productsData", options: () => ({fetchPolicy: "network-only", variables: {}})}),
  graphql(GET_PURCHASE_ORDER, {
    name: "orderData",
    skip: (props) => !props.purchaseOrderId,
    options: (props) => ({fetchPolicy: "network-only", variables: {id: props.purchaseOrderId}})
  }),
  graphql(CREATE_PURCHASE_ORDER, {props: ({mutate}) => ({createPurchaseOrder: (data) => mutate({variables: {data}})})}),
  graphql(UPDATE_PURCHASE_ORDER, {props: ({mutate}) => ({updatePurchaseOrder: (data) => mutate({variables: {data}})})}),
);

export default connect(null, {cancelCreatePurchaseOrder, cancelEditPurchaseOrder, successEditPurchaseOrder})(
  withRouter(withData(PurchaseOrderFormContainer))
);
